import React, { createContext, useContext, useMemo, ReactNode } from 'react';

/**
 * The visitor's likely country, from the browser's time zone.
 *
 * This used to call the get-geolocation edge function, which asked ipapi.co.
 * The free tier answered 429 most of the time, every call cost a network round
 * trip, and the only consumer is the country prefill in onboarding. The time
 * zone is known instantly, needs no request and no third party, and is right
 * for the countries that matter here. When it is not in the map the country
 * stays empty and the user picks it.
 */
export interface GeolocationData {
  country_code: string;
  country_name: string;
  city: string;
  region: string;
  timezone: string;
  currency: string;
  isLoading: boolean;
  error: string | null;
}

const TIMEZONE_COUNTRY: Record<string, { country_code: string; country_name: string; currency: string }> = {
  'Europe/Athens': { country_code: 'GR', country_name: 'Greece', currency: 'EUR' },
  'Asia/Nicosia': { country_code: 'CY', country_name: 'Cyprus', currency: 'EUR' },
  'Europe/Nicosia': { country_code: 'CY', country_name: 'Cyprus', currency: 'EUR' },
  'Europe/London': { country_code: 'GB', country_name: 'United Kingdom', currency: 'GBP' },
  'Europe/Dublin': { country_code: 'IE', country_name: 'Ireland', currency: 'EUR' },
  'Europe/Lisbon': { country_code: 'PT', country_name: 'Portugal', currency: 'EUR' },
  'Europe/Paris': { country_code: 'FR', country_name: 'France', currency: 'EUR' },
  'Europe/Berlin': { country_code: 'DE', country_name: 'Germany', currency: 'EUR' },
  'Europe/Rome': { country_code: 'IT', country_name: 'Italy', currency: 'EUR' },
  'Europe/Madrid': { country_code: 'ES', country_name: 'Spain', currency: 'EUR' },
  'Europe/Amsterdam': { country_code: 'NL', country_name: 'Netherlands', currency: 'EUR' },
  'Europe/Brussels': { country_code: 'BE', country_name: 'Belgium', currency: 'EUR' },
  'Europe/Luxembourg': { country_code: 'LU', country_name: 'Luxembourg', currency: 'EUR' },
  'Europe/Vienna': { country_code: 'AT', country_name: 'Austria', currency: 'EUR' },
  'Europe/Zurich': { country_code: 'CH', country_name: 'Switzerland', currency: 'CHF' },
  'Europe/Stockholm': { country_code: 'SE', country_name: 'Sweden', currency: 'SEK' },
  'Europe/Oslo': { country_code: 'NO', country_name: 'Norway', currency: 'NOK' },
  'Europe/Copenhagen': { country_code: 'DK', country_name: 'Denmark', currency: 'DKK' },
  'Europe/Helsinki': { country_code: 'FI', country_name: 'Finland', currency: 'EUR' },
  'Europe/Tallinn': { country_code: 'EE', country_name: 'Estonia', currency: 'EUR' },
  'Europe/Riga': { country_code: 'LV', country_name: 'Latvia', currency: 'EUR' },
  'Europe/Vilnius': { country_code: 'LT', country_name: 'Lithuania', currency: 'EUR' },
  'Europe/Warsaw': { country_code: 'PL', country_name: 'Poland', currency: 'PLN' },
  'Europe/Prague': { country_code: 'CZ', country_name: 'Czech Republic', currency: 'CZK' },
  'Europe/Bratislava': { country_code: 'SK', country_name: 'Slovakia', currency: 'EUR' },
  'Europe/Ljubljana': { country_code: 'SI', country_name: 'Slovenia', currency: 'EUR' },
  'Europe/Zagreb': { country_code: 'HR', country_name: 'Croatia', currency: 'EUR' },
  'Europe/Belgrade': { country_code: 'RS', country_name: 'Serbia', currency: 'RSD' },
  'Europe/Budapest': { country_code: 'HU', country_name: 'Hungary', currency: 'HUF' },
  'Europe/Bucharest': { country_code: 'RO', country_name: 'Romania', currency: 'RON' },
  'Europe/Sofia': { country_code: 'BG', country_name: 'Bulgaria', currency: 'BGN' },
  'Europe/Malta': { country_code: 'MT', country_name: 'Malta', currency: 'EUR' },
  'Europe/Istanbul': { country_code: 'TR', country_name: 'Turkey', currency: 'TRY' },
  'America/New_York': { country_code: 'US', country_name: 'United States', currency: 'USD' },
  'America/Chicago': { country_code: 'US', country_name: 'United States', currency: 'USD' },
  'America/Denver': { country_code: 'US', country_name: 'United States', currency: 'USD' },
  'America/Phoenix': { country_code: 'US', country_name: 'United States', currency: 'USD' },
  'America/Los_Angeles': { country_code: 'US', country_name: 'United States', currency: 'USD' },
  'America/Toronto': { country_code: 'CA', country_name: 'Canada', currency: 'CAD' },
  'America/Vancouver': { country_code: 'CA', country_name: 'Canada', currency: 'CAD' },
  'America/Mexico_City': { country_code: 'MX', country_name: 'Mexico', currency: 'MXN' },
  'America/Sao_Paulo': { country_code: 'BR', country_name: 'Brazil', currency: 'BRL' },
  'Australia/Sydney': { country_code: 'AU', country_name: 'Australia', currency: 'AUD' },
  'Australia/Melbourne': { country_code: 'AU', country_name: 'Australia', currency: 'AUD' },
  'Pacific/Auckland': { country_code: 'NZ', country_name: 'New Zealand', currency: 'NZD' },
  'Asia/Tokyo': { country_code: 'JP', country_name: 'Japan', currency: 'JPY' },
  'Asia/Shanghai': { country_code: 'CN', country_name: 'China', currency: 'CNY' },
  'Asia/Hong_Kong': { country_code: 'HK', country_name: 'Hong Kong', currency: 'HKD' },
  'Asia/Singapore': { country_code: 'SG', country_name: 'Singapore', currency: 'SGD' },
  'Asia/Seoul': { country_code: 'KR', country_name: 'South Korea', currency: 'KRW' },
  'Asia/Dubai': { country_code: 'AE', country_name: 'United Arab Emirates', currency: 'AED' },
  'Asia/Kolkata': { country_code: 'IN', country_name: 'India', currency: 'INR' },
  'Asia/Jerusalem': { country_code: 'IL', country_name: 'Israel', currency: 'ILS' },
};

export function geolocationFromTimezone(timezone?: string): GeolocationData {
  let tz = timezone ?? '';
  if (!tz) {
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { tz = ''; }
  }
  const match = TIMEZONE_COUNTRY[tz];
  return {
    country_code: match?.country_code ?? '',
    country_name: match?.country_name ?? '',
    city: '',
    region: '',
    timezone: tz,
    currency: match?.currency ?? 'EUR',
    isLoading: false,
    error: null,
  };
}

const GeolocationContext = createContext<GeolocationData>(geolocationFromTimezone());

export const useGeolocation = () => useContext(GeolocationContext);

export const GeolocationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const value = useMemo(() => geolocationFromTimezone(), []);
  return <GeolocationContext.Provider value={value}>{children}</GeolocationContext.Provider>;
};

export default GeolocationContext;
